import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Play, Check, Rocket, FileText, Inbox, Layers, X
} from 'lucide-react';
import {
  TUTORIALES,
  RUTAS,
  miniatura,
  duracionEnSegundos,
  formatoLargo,
  leerProgreso,
  estadoDe,
  progresoDeRuta,
  continuarCon
} from './tutorialesData';
import './tutoriales22.scss';

const ICONO_RUTA = { rocket: Rocket, search: Search, doc: FileText };

const NIVELES = ['Todos', 'Básico', 'Intermedio', 'Avanzado'];

const Tutoriales = () => {
  const navigate = useNavigate();

  const [progreso, setProgreso] = useState(() => leerProgreso());
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('Todos');
  const [nivel, setNivel] = useState('Todos');
  const [rutaSel, setRutaSel] = useState(null);

  // El progreso puede haber cambiado en la pestaña del reproductor
  useEffect(() => {
    const refrescar = () => setProgreso(leerProgreso());
    window.addEventListener('focus', refrescar);
    window.addEventListener('storage', refrescar);
    return () => {
      window.removeEventListener('focus', refrescar);
      window.removeEventListener('storage', refrescar);
    };
  }, []);

  const categorias = useMemo(() => {
    const mapa = new Map();
    TUTORIALES.forEach((t) => mapa.set(t.category, (mapa.get(t.category) || 0) + 1));
    return Array.from(mapa.entries());
  }, []);

  const totalSegundos = useMemo(
    () => TUTORIALES.reduce((acc, t) => acc + duracionEnSegundos(t.duration), 0),
    []
  );

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const deRuta = rutaSel ? RUTAS.find((r) => r.id === rutaSel)?.slugs || [] : null;

    return TUTORIALES.filter((t) => {
      if (deRuta && !deRuta.includes(t.slug)) return false;
      if (categoria !== 'Todos' && t.category !== categoria) return false;
      if (nivel !== 'Todos' && t.level !== nivel) return false;
      if (!q) return true;
      return (
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [busqueda, categoria, nivel, rutaSel]);

  const abrir = (slug) => navigate(`/tutoriales/${slug}`);

  const continuar = () => {
    const t = continuarCon(progreso);
    if (t) abrir(t.slug);
  };

  const siguiente = continuarCon(progreso);
  const hayFiltros = categoria !== 'Todos' || nivel !== 'Todos' || rutaSel || busqueda.trim();

  return (
    <div className="tut22">
      {/* ── Cabecera ─────────────────────────────────────── */}
      <div className="tut22-head">
        <div style={{ minWidth: 0 }}>
          <div className="tut22-eyebrow">APRENDE A LICITAR</div>
          <h1 className="tut22-title">Tutoriales</h1>
          <div className="tut22-sub">
            {TUTORIALES.length} videos{TUTORIALES.every((t) => t.duration) && ' · ' + formatoLargo(totalSegundos)} · de la primera búsqueda a la
            presentación de la oferta
          </div>
        </div>

        <div className="tut22-head-actions">
          <div className="tut22-search">
            <Search size={16} />
            <input
              placeholder="Buscar un tutorial"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: '#8a94a6' }}
                title="Limpiar"
              >
                <X size={15} />
              </button>
            )}
          </div>
          <button className="tut22-btn tut22-btn-primary" onClick={continuar} disabled={!siguiente}>
            <Play size={16} />
            {siguiente && estadoDe(progreso, siguiente.slug) === 'medias'
              ? 'Continuar donde quedé'
              : 'Empezar a ver'}
          </button>
        </div>
      </div>

      {/* ── Rutas de aprendizaje ─────────────────────────── */}
      <div className="tut22-rutas">
        {RUTAS.map((ruta) => {
          const Icono = ICONO_RUTA[ruta.icono] || Rocket;
          const p = progresoDeRuta(progreso, ruta);
          const activa = rutaSel === ruta.id;
          return (
            <button
              key={ruta.id}
              className={`tut22-ruta ${activa ? 'is-on' : ''}`}
              onClick={() => {
                setRutaSel(activa ? null : ruta.id);
                setCategoria('Todos');
              }}
              title={activa ? 'Quitar el filtro de esta ruta' : 'Ver solo los videos de esta ruta'}
            >
              <div className="tut22-ruta-top">
                <Icono size={18} />
                <strong>Ruta: {ruta.nombre}</strong>
              </div>
              <div className="tut22-ruta-desc">{ruta.descripcion}</div>
              <div className="tut22-ruta-foot">
                <span className="tut22-bar">
                  <span style={{ width: `${p.pct}%` }} />
                </span>
                <b>
                  {p.hechos} / {p.total}
                </b>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Filtros ──────────────────────────────────────── */}
      <div className="tut22-filtros">
        <button
          className={`tut22-chip ${categoria === 'Todos' ? 'is-on' : ''}`}
          onClick={() => setCategoria('Todos')}
        >
          Todos · {TUTORIALES.length}
        </button>
        {categorias.map(([nombre, n]) => (
          <button
            key={nombre}
            className={`tut22-chip ${categoria === nombre ? 'is-on' : ''}`}
            onClick={() => setCategoria(categoria === nombre ? 'Todos' : nombre)}
          >
            {nombre} · {n}
          </button>
        ))}

        <span className="tut22-spacer" />

        <label className="tut22-select">
          <Layers size={16} />
          Nivel:
          <select value={nivel} onChange={(e) => setNivel(e.target.value)}>
            {NIVELES.map((n) => (
              <option key={n} value={n}>
                {n === 'Todos' ? 'todos' : n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* ── Rejilla ──────────────────────────────────────── */}
      <div className="tut22-grid">
        {visibles.length === 0 ? (
          <div className="tut22-empty">
            <div className="tut22-empty-ic">
              <Inbox size={22} />
            </div>
            <h4>Ningún tutorial con estos filtros</h4>
            <p>Prueba con otra categoría, otro nivel o borra la búsqueda.</p>
            {hayFiltros && (
              <div style={{ marginTop: 14 }}>
                <button
                  className="tut22-btn"
                  onClick={() => {
                    setCategoria('Todos');
                    setNivel('Todos');
                    setRutaSel(null);
                    setBusqueda('');
                  }}
                >
                  <X size={15} /> Limpiar filtros
                </button>
              </div>
            )}
          </div>
        ) : (
          visibles.map((t) => {
            const estado = estadoDe(progreso, t.slug);
            const pct = progreso[t.slug]?.pct || 0;
            return (
              <button key={t.slug} className="tut22-card" onClick={() => abrir(t.slug)}>
                <div className="tut22-thumb">
                  <img src={miniatura(t.videoId)} alt="" loading="lazy" />
                  <span className="tut22-thumb-play">
                    <Play size={20} fill="currentColor" />
                  </span>
                  {t.duration && <span className="tut22-thumb-dur">{t.duration}</span>}
                  {t.nuevo && <span className="tut22-thumb-new">NUEVO</span>}
                  {pct > 0 && (
                    <span className="tut22-thumb-bar">
                      <span style={{ width: `${Math.min(100, pct)}%` }} />
                    </span>
                  )}
                </div>

                <div className="tut22-card-body">
                  <div className="tut22-card-top">
                    <span className="tut22-cat">{t.category}</span>
                    {estado === 'visto' ? (
                      <span className="tut22-estado e-visto">
                        <Check size={12} /> Visto
                      </span>
                    ) : estado === 'medias' ? (
                      <span className="tut22-estado e-medias">A medias</span>
                    ) : (
                      <span className="tut22-estado e-nivel">{t.level}</span>
                    )}
                  </div>

                  <div className="tut22-card-title">{t.title}</div>
                  <div className="tut22-card-meta">
                    {estado === 'nuevo' ? '' : `Nivel ${t.level.toLowerCase()} · `}
                    {t.views != null ? t.views.toLocaleString('es-PE') + ' visualizaciones' : ''}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {rutaSel && (
        <div style={{ marginTop: 14 }}>
          <button className="tut22-btn" onClick={() => setRutaSel(null)}>
            <X size={15} /> Ver todos los tutoriales, no solo la ruta
          </button>
        </div>
      )}
    </div>
  );
};

export default Tutoriales;
